import React, { useCallback } from "react";

import FileDropZone from "@/components/reusables/FileDropzone";

interface DriveDropzoneProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  onFolderDrop: (entries: FileSystemEntry[]) => void;
  isLoading?: boolean;
}

export const DriveDropzone: React.FC<DriveDropzoneProps> = ({
  files,
  onFilesChange,
  onFolderDrop,
  isLoading = false,
}) => {
  const handleDropzoneDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>): void => {
      event.preventDefault();
      event.stopPropagation();

      // Check if we have items that might be folders
      if (event.dataTransfer?.items && event.dataTransfer.items.length > 0) {
        const entries: FileSystemEntry[] = [];

        for (let i = 0; i < event.dataTransfer.items.length; i++) {
          const item = event.dataTransfer.items[i];
          const entry = item.webkitGetAsEntry();

          if (entry) {
            entries.push(entry);
          }
        }

        if (entries.length > 0) {
          onFolderDrop(entries);

          return;
        }
      }

      // If no folder structure detected, handle as regular files
      const fileList: File[] = [];
      if (event.dataTransfer?.files) {
        for (let i = 0; i < event.dataTransfer.files.length; i++) {
          fileList.push(event.dataTransfer.files[i]);
        }

        if (fileList.length > 0) {
          onFilesChange(fileList);
        }
      }
    },
    [onFilesChange, onFolderDrop],
  );

  return (
    <div
      onDrop={handleDropzoneDrop}
      onDragOver={(e) => e.preventDefault()}
      className={
        isLoading ? "pointer-events-none opacity-75 relative" : "relative"
      }
    >
      <FileDropZone
        files={files}
        setFiles={onFilesChange}
        type='box'
        resetDragState={isLoading}
      />
      {isLoading && (
        <div className='absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg'>
          <div className='flex items-center space-x-2 text-blue-600 font-medium'>
            <div className='animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent'></div>
            <span>Uploading...</span>
          </div>
        </div>
      )}
    </div>
  );
};
